<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application.localName || application.title"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-name="application.localName || application.title"
      :local-title="
        $t('titles.requestAuthorization', {
          applicationName: application.localName || application.title,
        })
      "
    />
    <div class="contentView">
      <b-table
        :data="listRightsRequests"
        :striped="true"
        default-sort="row.name"
        default-sort-direction="asc"
        height="100%"
        sort-icon="arrow-up"
        sort-icon-size="is-small"
        sortable
        class="column table-auth"
      >
        <b-table-column
          :searchable="true"
          field="view"
          :label="$t('requestAuthorization.show-right-request')"
          :aria-label="$t('requestAuthorization.show-right-request')"
          style="min-width: 15px"
          sortable
        >
          <template #searchable>
            <b-select
              v-model="filterState"
              :placeholder="filterStates[filterState].label"
              :aria-label="filterStates[filterState].label"
              @update:model-value="isVisibleRequest(filterState)"
            >
              <option
                v-for="(option, id) in filterStates"
                :key="id"
                :value="id"
                :aria-label="option.label"
              >
                {{ option.label }}
              </option>
            </b-select>
          </template>
          <template #default="props">
            <!-- Une seule icone par ligne, mutuellement exclusive selon le
                 statut de la demande : crayon = a traiter ( editable ),
                 oeil = deja traitee ( consultation lecture seule, pas de
                 reprise editoriale possible ; verrou applique aussi cote
                 backend dans RightsRequestService.treat ). Wrapper flex
                 centre l'icone dans la cellule b-table. -->
            <div class="row-action-cell">
              <b-tooltip
                v-if="!props.row.setted"
                :label="$t('requestAuthorization.row-action-edit-tooltip')"
                position="is-right"
              >
                <a
                  class="bTableIcon"
                  :aria-label="$t('requestAuthorization.row-action-edit-tooltip')"
                  @click="treatmentRequest(props.row.id, false)"
                >
                  <b-icon
                    icon="file-pen"
                    type="is-warning"
                    size="is-small"
                  />
                </a>
              </b-tooltip>
              <b-tooltip
                v-else
                :label="$t('requestAuthorization.row-action-view-tooltip')"
                position="is-right"
              >
                <a
                  class="bTableIcon"
                  :aria-label="$t('requestAuthorization.row-action-view-tooltip')"
                  @click="treatmentRequest(props.row.id, true)"
                >
                  <b-icon
                    icon="eye"
                    type="is-primary"
                    size="is-small"
                  />
                </a>
              </b-tooltip>
            </div>
          </template>
        </b-table-column>
        <b-table-column
          :searchable="true"
          field="createDate"
          :label="$t('requestAuthorization.request-date-create')"
          :aria-label="$t('requestAuthorization.request-date-create')"
          header-class="request"
          sortable
          :custom-search="searchCreateDate"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
            />
          </template>
          <template #default="props">
            <p
              class="is-flex is-align-content-center bTableIcon"
              :aria-label="formatDate(props.row.createDate)"
            >
              {{ formatDate(props.row.createDate) }}
            </p>
          </template>
        </b-table-column>
        <b-table-column
          :searchable="true"
          field="name"
          :label="$t('requestAuthorization.request-user-name')"
          :aria-label="$t('requestAuthorization.request-user-name')"
          sortable
          header-class="request"
          :custom-search="searchUserLogin"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
            />
          </template>
          <template #default="props">
            <p
              v-if="props.row.user"
              :aria-label="getUserLabel(props.row.user)"
            >
              {{ getUserLabel(props.row.user) }}
            </p>
          </template>
        </b-table-column>
        <b-table-column
          :searchable="true"
          field="email"
          :label="$t('requestAuthorization.request-user-email')"
          :aria-label="$t('requestAuthorization.request-user-email')"
          sortable
          header-class="request"
          :custom-search="searchUserEmail"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
            />
          </template>
          <template #default="props">
            <p
              v-if="props.row.user"
              :aria-label="getUserEmail(props.row.user)"
            >
              {{ getUserEmail(props.row.user) }}
            </p>
          </template>
        </b-table-column>
        <b-table-column
          :searchable="true"
          field="updateDate"
          :label="$t('requestAuthorization.request-date-validate')"
          :aria-label="$t('requestAuthorization.request-date-validate')"
          sortable
          header-class="treatment"
          :custom-search="searchUpdateDate"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
            />
          </template>
          <template #default="props">
            <p
              class="is-flex is-align-content-center bTableIcon"
              :aria-label="props.row.setted ? formatDateTime(props.row.updateDate) : ''"
            >
              {{ props.row.setted ? formatDateTime(props.row.updateDate) : "" }}
            </p>
          </template>
        </b-table-column>
        <b-table-column
          :searchable="true"
          field="name_manager"
          :label="$t('requestAuthorization.request-manager-user-name')"
          :aria-label="$t('requestAuthorization.request-manager-user-name')"
          sortable
          header-class="treatment"
          :custom-search="searchManagerLogin"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
            />
          </template>
          <template #default="props">
            <p class="is-flex is-align-content-center">
              <span
                class="with-padding"
                :aria-label="props.row?.treatedByLogin || props.row?.manager_user_name"
              >{{ props.row?.treatedByLogin || props.row?.manager_user_name }}</span>
            </p>
          </template>
        </b-table-column>
      </b-table>
    </div>
  </PageView>
</template>

<script lang="ts">
import dayjs from "@/plugins/dayjs";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import PageView from "../../common/PageView.vue";
import services from "@/composable/services";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import useObject from "@/composable/components/object";
import { onMounted, ref, watch } from "vue";
import useArray from "@/composable/components/array";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import { BIcon, BInput, BSelect, BTable, BTableColumn, BTooltip } from "buefy";
import "@/style/views/authorization/right_request/RequestAuthorizationManagementView.scss";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { ErrorType } from "@/composable/application/errors";
import { ApplicationUserResult, GetRightsRequestResult, RightsRequestResult } from "@/types/generated/UsedGeneratedTypes";

export default {
  name: "RequestAuthorizationManagementView",
  components: {
    BTable,
    BSelect,
    BInput,
    BIcon,
    BTableColumn,
    BTooltip,
    TitleAndDescription,
    PageView,
    SubMenu,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const { shallowRefArray: listAllUsers, doChangeArray: changeListAllUsers } = useArray([] as ApplicationUserResult[]);
    const { reactiveObject: rightsRequests, doChangeObject: changeRightsRequests } = useObject(new Object() as GetRightsRequestResult);
    const { shallowRefArray: listRightsRequests, doChangeArray: changeListRightsRequests }
      = useArray([] as RightsRequestResult[]);
    let filterState = ref(2);
    watch(
      () => filterState,
      (value) => {
        if (value.value === 1) {
          changeListRightsRequests(
            rightsRequests.rightsRequests.filter(request => request.setted === false),
          );
        }
        else if (value.value === 0) {
          changeListRightsRequests(
            rightsRequests.rightsRequests.filter(request => request.setted === true),
          );
        }
        else {
          changeListRightsRequests(rightsRequests.rightsRequests);
        }
      },
    );
    let filterStates = [
      {
        type: "close",
        label: t("dataTypeAuthorizations.filterScope.close"),
      },
      {
        type: "open",
        label: t("dataTypeAuthorizations.filterScope.open"),
      },
      {
        type: "all",
        label: t("dataTypeAuthorizations.filterScope.all"),
      },
    ];

    onMounted(async () => {
      await init();
      changeSubMenuPaths([
        new SubMenuPath(
          t("requestAuthorization.list-request"),
          () => {},
          () => {
            router.push(`/applications/${props.applicationName}`);
          },
        ),
      ]);
    });

    function getDate(value: string | number | Date | null | undefined) {
      return dayjs(value).format("DD/MM/YYYY");
    }

    function getUserEmail(id: string) {
      let userEmail = t("requestAuthorization.unknow-label-user");
      const authenticatedUser = services.loginService.getStoredAuthenticatedUser();
      if (authenticatedUser?.id === id) {
        userEmail = authenticatedUser.email;
      }
      else {
        listAllUsers.value.forEach((user) => {
          if (user.id === id) {
            userEmail = user.email;
          }
        });
      }
      return userEmail;
    }

    function getUserLabel(id: string) {
      let userLabel = t("requestAuthorization.unknow-label-user");
      const authenticatedUser = services.loginService.getStoredAuthenticatedUser();
      if (authenticatedUser?.id === id) {
        userLabel = authenticatedUser.login;
      }
      else {
        listAllUsers.value.forEach((user) => {
          if (user.id === id) {
            userLabel = user.label;
          }
        });
      }
      return userLabel;
    }

    async function init() {
      try {
        const getApplication = await services.applicationService.getApplication(
          props.applicationName,
          ["DATATYPE", "REFERENCETYPE", "CONFIGURATION", "RIGHTSREQUEST"],
        );
        if (getApplication) {
          changeApplication(getApplication);
        }
        let localRightsRequests = await services.requestRightsService.getRightsRequests(
          props.applicationName,
        );
        for (const request of localRightsRequests.rightsRequests) {
          request.children = [{}];
          request.label = request.id;
        }
        let localeListAllUsers = await services.authorizationService.getAuthorizationGrantableInfos(
          props.applicationName,
        );
        changeListAllUsers(localeListAllUsers.users.filter(user => user.label !== "_public_"));
        if (localRightsRequests.rightsRequests) {
          changeListRightsRequests(localRightsRequests.rightsRequests);
        }
        changeRightsRequests(localRightsRequests);
      }
      catch (error) {
        services.alertService.toastServerError(error as ErrorType);
      }
    }

    function isVisibleRequest(setted: number) {
      let filterRiquest = rightsRequests.rightsRequests;
      if (setted === 1) {
        filterRiquest
          = rightsRequests.rightsRequests.filter(request => request.setted === false) || [];
        changeListRightsRequests(filterRiquest);
      }
      else if (setted === 0) {
        filterRiquest
          = rightsRequests.rightsRequests.filter(request => request.setted === true) || [];
        changeListRightsRequests(filterRiquest);
      }
      else {
        changeListRightsRequests(filterRiquest);
      }
    }

    function manageRequest(id: string) {
      router.push(`/applications/${props.applicationName}/authorizationsRequest/${id}`);
    }

    /**
     * Navigation vers la page de traitement d'une demande.
     * Lorsque la demande est déjà traitée ( {@code setted=true} ), on
     * passe la query string {@code readOnly=1} ; le composant cible
     * désactive alors le formulaire pour proposer une consultation pure.
     */
    function treatmentRequest(id: string, readOnly: boolean = false) {
      router.push({
        path: `/applications/${props.applicationName}/authorizationsRequest/treatment/${id}`,
        query: readOnly ? { readOnly: "1" } : {},
      });
    }
    function formatDate(date: string | number | Date | null | undefined) {
      if (!date) return "";
      return dayjs(date).format("DD/MM/YYYY HH:mm");
    }
    /**
     * Format date + heure ( colonne "Date et heure de validation" ).
     * On garde formatDate pour les colonnes purement date ( ex. creation ).
     */
    function formatDateTime(date: string | number | Date | null | undefined) {
      if (!date) return "";
      return dayjs(date).format("DD/MM/YYYY HH:mm");
    }

    /**
     * Filtres de recherche par colonne. Buefy compare par défaut le terme
     * saisi à la valeur de `row[field]` ; or les colonnes "name", "email"
     * et "name_manager" n'ont pas de propriété directe sur la row ( elles
     * sont résolues côté frontend via getUserLabel / getUserEmail /
     * treatedByLogin ). Idem pour les dates qu'il faut formater dans la
     * locale UI avant comparaison.
     *
     * Toutes les comparaisons sont insensibles à la casse, sur la
     * représentation effectivement affichée à l'utilisateur.
     */
    function matches(value: string | null | undefined, term: string | number): boolean {
      const t = String(term ?? "").toLowerCase();
      if (!t) return true;
      return (value ?? "").toLowerCase().includes(t);
    }
    // Signatures alignées sur Buefy CustomSearchFunction :
    //   ( row: TableRow=any , input: string | number ) => boolean
    function searchUserLogin(row: any, input: string | number): boolean {
      return matches(row.user ? getUserLabel(row.user) : "", input);
    }
    function searchUserEmail(row: any, input: string | number): boolean {
      return matches(row.user ? getUserEmail(row.user) : "", input);
    }
    function searchManagerLogin(row: any, input: string | number): boolean {
      return matches(row.treatedByLogin || row.manager_user_name || "", input);
    }
    function searchCreateDate(row: any, input: string | number): boolean {
      return matches(formatDate(row.createDate), input);
    }
    function searchUpdateDate(row: any, input: string | number): boolean {
      return matches(row.setted ? formatDateTime(row.updateDate) : "", input);
    }

    return {
      formatDate,
      formatDateTime,
      manageRequest,
      treatmentRequest,
      isVisibleRequest,
      getUserEmail,
      getUserLabel,
      getDate,
      subMenuPaths,
      filterStates,
      filterState,
      listAllUsers,
      rightsRequests,
      application,
      listRightsRequests,
      searchUserLogin,
      searchUserEmail,
      searchManagerLogin,
      searchCreateDate,
      searchUpdateDate,
    };
  },
};
</script>
